import { useEffect, useState } from "preact/hooks";
import { batch } from "@preact/signals";
import { describe, loadFacet, type Bucket, type FacetState } from "../../state/explore";
import { selectedComposerId, setPieceFilter } from "../../state/store";

const HEAD = 8;
const MAX = 24;

/** The pieces under one composer row — the tap-to-expand list the first Explore tab had
 *  and the overhaul dropped. Same slice as the panel around it (every active filter
 *  applies), narrowed to this composer, so the counts are the counts the reader would get
 *  by drilling — without leaving the composer list. Picking a piece scopes to both the
 *  piece and the composer, as the original did. */
export function ComposerPieces({
  composerId,
  composerName,
  onDrill,
}: {
  composerId: number;
  composerName: string;
  onDrill?: () => void;
}) {
  const base = describe("piece", { limit: MAX, own: "only" });
  const key = JSON.stringify(base);
  const [state, setState] = useState<FacetState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const filters: typeof base.filters = {};
    for (const era of Object.keys(base.filters) as Array<keyof typeof base.filters>) {
      filters[era] = { ...base.filters[era]!, composer: composerId };
    }
    setError(null);
    loadFacet({ ...base, filters }, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setState(res);
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : String(err));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, composerId]);

  function pick(b: Bucket) {
    batch(() => {
      selectedComposerId.value = composerId;
      setPieceFilter({
        key: b.key,
        label: b.label ?? b.key,
        composer: composerName,
        composer_id: composerId,
        part_kind: null,
        count: b.n,
      });
    });
    onDrill?.();
  }

  function all() {
    selectedComposerId.value = composerId;
    onDrill?.();
  }

  if (error) return <p class="xp-note xp-sub-note">Couldn't load pieces: {error}</p>;
  if (!state) return <p class="xp-note xp-sub-note">Loading pieces…</p>;
  const buckets = state.buckets;
  if (buckets.length === 0) return <p class="xp-note xp-sub-note">No named pieces in this slice.</p>;
  const visible = expanded ? buckets : buckets.slice(0, HEAD);
  const maxN = visible.reduce((m, b) => Math.max(m, b.n), 1);
  const hidden = buckets.length - visible.length;

  return (
    <div class="xp-sub">
      <ol class="xp-rows xp-rows-sub">
        {visible.map((b) => (
          <li key={b.key}>
            <button type="button" class="xp-row xp-row-wrap xp-row-sub" onClick={() => pick(b)} title={`Narrow to ${b.label ?? b.key}`}>
              <span class="xp-row-main">
                <span class="xp-row-name">{b.label ?? b.key}</span>
                <span class="xp-bar-track">
                  <span class="xp-bar" style={{ width: `${Math.max(1.5, (b.n / maxN) * 100)}%` }} />
                </span>
              </span>
              <span class="xp-row-num">
                <span class="xp-row-n">{b.n.toLocaleString()}</span>
              </span>
            </button>
          </li>
        ))}
      </ol>
      <div class="xp-sub-foot">
        {hidden > 0 && (
          <button type="button" class="xp-link-btn" onClick={() => setExpanded(true)}>
            Show {hidden} more
          </button>
        )}
        <button type="button" class="xp-link-btn" onClick={all}>
          All of {composerName} →
        </button>
      </div>
    </div>
  );
}
